import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney, formatYearMonth } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field, SummaryStat } from "@/pages/Income";
import { Plus, Trash2 } from "lucide-react";

const CATEGORIES = ["Groceries","Dining","Transport","Entertainment","Utilities","Shopping","Healthcare","Education","Travel","Coffee","Other"];

export default function Budget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const ym = new Date().toISOString().slice(0,7);
  const [form, setForm] = useState({ category:"Groceries", monthly_limit:"", month: ym });

  const load = async () => {
    const [b, e] = await Promise.all([api.get("/budgets"), api.get("/expenses")]);
    setItems(b.data); setExpenses(e.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { category: form.category, monthly_limit: parseFloat(form.monthly_limit), month: form.month };
    try { await api.post("/budgets", payload); toast.success("Budget added"); setOpen(false); load(); }
    catch { toast.error(t("common.toast_failed")); }
  };
  const remove = async (id) => { if (!confirm("Delete budget?")) return; await api.delete(`/budgets/${id}`); load(); };

  const cur = user?.currency || "USD";
  const budgets = items.filter(b => b.month === ym);
  const spent = (cat) => expenses
    .filter(e => (e.date||"").startsWith(ym) && e.category === cat)
    .reduce((a,b) => a + Number(b.amount||0), 0);
  const totalBudget = budgets.reduce((a,b) => a + Number(b.monthly_limit||0), 0);
  const totalSpent = budgets.reduce((a,b) => a + spent(b.category), 0);

  return (
    <div>
      <PageHeader
        subtitle={`${t("pages.budget.subtitle")} · ${formatYearMonth(ym)}`}
        title={t("pages.budget.title")}
        action={<button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-budget-btn"><Plus size={16}/> {t("common.add")}</button>}
      />

      <div className="grid md:grid-cols-3 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.budget.total_budget")} value={formatMoney(totalBudget, cur)}/>
        <SummaryStat label={t("pages.budget.total_spent")} value={formatMoney(totalSpent, cur)}/>
        <SummaryStat label={t("pages.budget.remaining")} value={formatMoney(totalBudget - totalSpent, cur)}/>
      </div>

      {budgets.length === 0 ? (
        <EmptyState title={t("pages.budget.empty_title")} body={t("pages.budget.empty_body")}
          action={<button className="btn-primary" onClick={() => setOpen(true)}><Plus size={16}/> {t("pages.budget.add_budget")}</button>}/>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {budgets.map(b => {
            const s = spent(b.category);
            const pct = Math.min(100, (s / b.monthly_limit * 100));
            const over = s > b.monthly_limit;
            return (
              <div key={b.id} className="card-flat p-5">
                <div className="flex items-center justify-between">
                  <div className="text-base font-medium" style={{fontFamily:'var(--font-heading)'}}>{b.category}</div>
                  <button className="btn-danger-ghost" onClick={() => remove(b.id)}><Trash2 size={15}/></button>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className={`num-lg ${over ? "text-[var(--accent-danger)]" : "text-[var(--text-primary)]"}`}>{formatMoney(s, cur)}</div>
                  <div className="text-sm text-[var(--text-muted)]">/ {formatMoney(b.monthly_limit, cur)}</div>
                </div>
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-[var(--bg-accent)] overflow-hidden">
                    <div className={`h-full ${over ? "bg-[var(--accent-danger)]" : "bg-[var(--accent-primary)]"}`} style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1.5">{over ? t("pages.budget.over_by", { amount: formatMoney(s - b.monthly_limit, cur) }) : t("pages.budget.remaining_pct", { pct: (100-pct).toFixed(0) })}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={t("pages.budget.add_title")}>
        <form onSubmit={save} className="grid gap-4">
          <Field label={t("common.category")}>
            <select className="field-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="input-budget-cat">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={t("pages.budget.monthly_limit", { cur })}><input type="number" step="0.01" required className="field-input" value={form.monthly_limit} onChange={e => setForm({...form, monthly_limit: e.target.value})} data-testid="input-budget-limit"/></Field>
          <Field label={t("pages.budget.month")}><input type="month" required className="field-input" value={form.month} onChange={e => setForm({...form, month: e.target.value})}/></Field>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-budget-btn">{t("common.add")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
